import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  CharacterSheetInterface,
  Character,
  Condition,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import LoadingBox from "@components/input/LoadingBox";
import ConditionAddRow from './ConditionAddRow';
// import DisplayString from '@components/input/strings/DisplayString';
import ConditionChip from './ConditionChip';

interface Props {
  char_int?: CharacterSheetInterface;
  active_conditions: { [id: string]: { level: number, source_true_id: string } };
  starred: string[];
  immune: string[];
  extra_ids?: string[];
  disabled: boolean;
  layout?: 'inline' | 'stacked';
  onChange: (condition_id: string, level: number, max_level: number) => void;
  onStar: (id: string) => void;
  onImmune: (id: string) => void;
}

interface PopoverPos { top: number; left: number; }

const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function ConditionBar({
  char_int,
  active_conditions,
  starred,
  immune,
  extra_ids = [],
  disabled,
  layout = 'inline',
  onChange,
  onStar,
  onImmune,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [localActive, setLocalActive] = useState<{ [id: string]: { level: number, source_true_id: string } }>(
    active_conditions
  );
  const [addOpen, setAddOpen] = useState(false);
  const [addPos, setAddPos] = useState<PopoverPos>({ top: 0, left: 0 });
  const [addSearch, setAddSearch] = useState('');
  const [addFilter, setAddFilter] = useState<'common' | 'rare' | 'all'>('common');
  const [levelPopoverId, setLevelPopoverId] = useState<string | null>(null);
  // const [levelPos, setLevelPos] = useState<PopoverPos>({ top: 0, left: 0 });
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const prevActiveRef = useRef(active_conditions);

  const load = useCallback(async () => {
    let new_conditions = await data_util.getConditions();
    if (char_int instanceof Character) {
      new_conditions = new_conditions.filter(o => common_util.anyOverlaps(o.source_book_ids, char_int.character_options.source_books));
      new_conditions = new_conditions.filter(o => !o.exhaustion || o._id === char_int.character_options.exhaustion_id);
    }
    setConditions(new_conditions);
    setLoading(false);
  }, [char_int]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  // Sync inbound prop changes (e.g. after recalc completes) back into local state,
  // but only when the prop reference actually changes.
  useEffect(() => {
    if (prevActiveRef.current !== active_conditions) {
      prevActiveRef.current = active_conditions;
      setLocalActive(active_conditions);
    }
  }, [active_conditions]);

  // Close popovers on outside click or scroll
  useEffect(() => {
    if (!addOpen && !levelPopoverId) return;
    const close = (e: MouseEvent | Event) => {
      const target = e.target as Element | null;
      if (target?.closest('[data-condition-popover]')) return;
      setAddOpen(false);
      setAddSearch('');
      setLevelPopoverId(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [addOpen, levelPopoverId]);

  if (!fetched || loading) {
    return <LoadingBox />;
  }

  const activeKeys = Object.keys(localActive);
  const activeConditions = conditions.filter(o => activeKeys.includes(o._id));

  const alternate_conditions = char_int
    ? char_int.alternate_condition_features.map(o => o.alternate_condition_id)
    : [];

  const addableConditions = conditions.filter(o => !activeKeys.includes(o._id));

  const filteredAddable = addableConditions.filter(o => {
    const nameMatch = o.use_name.toLowerCase().includes(addSearch.toLowerCase());
    if (!nameMatch) return false;
    if (addFilter === 'common') {
      return o.common || alternate_conditions.includes(o._id)
        || common_util.anyOverlaps(o.class_ids, extra_ids)
        || common_util.anyOverlaps(o.subclass_ids, extra_ids);
    } else if (addFilter === 'rare') {
      return !o.common && !alternate_conditions.includes(o._id)
        && !common_util.anyOverlaps(o.class_ids, extra_ids)
        && !common_util.anyOverlaps(o.subclass_ids, extra_ids);
    }
    return true;
  });

  const getLevel = (condId: string): number => {
    return localActive[condId]?.level ?? 0;
  };

  const openAddDropdown = () => {
    if (!addButtonRef.current) return;
    const rect = addButtonRef.current.getBoundingClientRect();
    const popoverWidth = 240;
    const centeredLeft = rect.left + rect.width / 2 - popoverWidth / 2;
    // Clamp so it doesn't go off either edge (8px margin)
    const clampedLeft = Math.max(8, Math.min(centeredLeft, window.innerWidth - popoverWidth - 8));
    setAddPos({ top: rect.bottom + 4, left: clampedLeft });
    setLevelPopoverId(null);
    setAddOpen(v => !v);
  };

  // const openLevelPopover = (condId: string, anchorEl: HTMLElement) => {
  //   const rect = anchorEl.getBoundingClientRect();
  //   setLevelPos({ top: rect.bottom + 4, left: rect.left });
  //   setAddOpen(false);
  //   setLevelPopoverId(prev => prev === condId ? null : condId);
  // };

  const handleAddCondition = (condId: string) => {
    const cond = conditions.find(o => o._id === condId);
    if (!cond) return;
    setLocalActive(prev => ({
      ...prev,
      [condId]: { level: 1, source_true_id: '' },
    }));
    onChange(condId, 1, cond.max_level);
    setAddOpen(false);
    setAddSearch('');
  };

  const handleRemoveCondition = (condId: string) => {
    const cond = conditions.find(o => o._id === condId);
    if (!cond) return;
    setLocalActive(prev => {
      const next = { ...prev };
      delete next[condId];
      return next;
    });
    onChange(condId, 0, cond.max_level);
  };

  const popoverBase: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
    background: '#000',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  };

  const renderLabelAndAdd = () => (
    <>
      <span className={ layout === "stacked" ? "centered" : "" } style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        <strong style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Conditions</strong>
        {!disabled && (
          <>
            <button
              ref={addButtonRef}
              data-condition-popover
              onClick={openAddDropdown}
              title="Add condition"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '1px solid #aaa',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '16px',
                lineHeight: 1,
                padding: 0,
                color: '#555',
              }}
            >
              +
            </button>
          </>
        )}
      </span>
      {addOpen && (
        <div
          data-condition-popover
          style={{
            ...popoverBase,
            top: addPos.top,
            left: addPos.left,
            width: '240px',
            padding: '8px',
          }}
        >
          <input
            autoFocus
            type="text"
            placeholder="Search conditions..."
            value={addSearch}
            onChange={e => setAddSearch(e.target.value)}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '4px 6px',
              fontSize: '12px',
              marginBottom: '6px',
            }}
          />
          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
            {(['common', 'rare', 'all'] as const).map(f => (
              <button
                key={f}
                onClick={() => setAddFilter(f)}
                style={{
                  flex: 1,
                  padding: '2px 0',
                  fontSize: '11px',
                  borderRadius: '3px',
                  border: '1px solid #ccc',
                  background: addFilter === f ? '#1976d2' : '#f5f5f5',
                  color: addFilter === f ? '#fff' : '#333',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredAddable.length === 0 && (
              <div style={{ fontSize: '12px', color: '#999', textAlign: 'center', padding: '8px' }}>
                No conditions found
              </div>
            )}
            {filteredAddable.sort((a, b) => a.name.localeCompare(b.name)).map(cond => (
              <ConditionAddRow key={cond._id}
                cond={cond} 
                onAdd={() => {
                  handleAddCondition(cond._id);
                }}
                onImmune={onImmune}
                onStar={onStar}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderChips = () => (
    <>
      {activeConditions.map(cond => {
        const level = getLevel(cond._id);

        return (
          <ConditionChip key={cond._id}
            cond={cond}
            level={level}
            disabled={disabled}
            onChange={(changed) => {
              if (changed === 0) {
                handleRemoveCondition(cond._id);
              } else {
                setLocalActive(prev => ({
                  ...prev,
                  [cond._id]: { ...prev[cond._id], level: changed },
                }));
                onChange(cond._id, changed, cond.max_level);
                setLevelPopoverId(null);
              }
            }}
            onImmune={onImmune}
            onStar={onStar}
          />
        );
        // return (
        //   <div key={cond._id} style={{ display: 'inline-flex' }}>
        //     <div
        //       data-condition-popover
        //       style={{
        //         display: 'inline-flex',
        //         alignItems: 'center',
        //         gap: '4px',
        //         padding: '2px 4px 2px 8px',
        //         borderRadius: '12px',
        //         background: '#e3f2fd',
        //         border: '1px solid #90caf9',
        //         fontSize: '12px',
        //         color: '#0d47a1',
        //         whiteSpace: 'nowrap',
        //       }}
        //     >
        //       <span
        //         onClick={e => {
        //           if (hasLevels && !disabled) {
        //             openLevelPopover(cond._id, e.currentTarget as HTMLElement);
        //           }
        //         }}
        //         style={{ cursor: hasLevels && !disabled ? 'pointer' : 'default' }}
        //       >
        //         {cond.use_name}
        //         {hasLevels && (
        //           <span style={{
        //             marginLeft: '4px',
        //             background: '#1976d2',
        //             color: '#fff',
        //             borderRadius: '8px',
        //             padding: '0 5px',
        //             fontSize: '11px',
        //             fontWeight: 'bold',
        //           }}>
        //             {level}
        //           </span>
        //         )}
        //       </span>

        //       {!disabled && (
        //         <span
        //           onClick={() => handleRemoveCondition(cond._id)}
        //           title="Remove"
        //           style={{
        //             cursor: 'pointer',
        //             color: '#5c8ac7',
        //             fontSize: '14px',
        //             lineHeight: 1,
        //             padding: '0 2px',
        //             borderRadius: '50%',
        //             display: 'inline-flex',
        //             alignItems: 'center',
        //           }}
        //           onMouseEnter={e => (e.currentTarget.style.color = '#b71c1c')}
        //           onMouseLeave={e => (e.currentTarget.style.color = '#5c8ac7')}
        //         >
        //           ×
        //         </span>
        //       )}
        //     </div>

        //     {isLevelOpen && hasLevels && (
        //       <div
        //         data-condition-popover
        //         style={{
        //           ...popoverBase,
        //           top: levelPos.top,
        //           left: levelPos.left,
        //           padding: '8px',
        //           minWidth: '100px',
        //         }}
        //       >
        //         <div style={{ fontSize: '11px', color: '#fff', marginBottom: '6px' }}>
        //           Set level
        //         </div>
        //         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        //           {Array.from({ length: cond.max_level + 1 }, (_, i) => i).map(lvl => (
        //             <button
        //               key={lvl}
        //               onClick={() => {
        //                 setLocalActive(prev => ({
        //                   ...prev,
        //                   [cond._id]: { ...prev[cond._id], level: lvl },
        //                 }));
        //                 onChange(cond._id, lvl, cond.max_level);
        //                 setLevelPopoverId(null);
        //               }}
        //               style={{
        //                 width: '28px',
        //                 height: '28px',
        //                 borderRadius: '4px',
        //                 border: '1px solid #ccc',
        //                 background: level === lvl ? '#1976d2' : '#f5f5f5',
        //                 color: level === lvl ? '#fff' : '#333',
        //                 cursor: 'pointer',
        //                 fontSize: '12px',
        //                 fontWeight: level === lvl ? 'bold' : 'normal',
        //               }}
        //             >
        //               {lvl}
        //             </button>
        //           ))}
        //         </div>
        //         <div style={{ fontSize: '11px', border: "1px solid", padding: "4px" }}>
        //           <DisplayString str={ cond.description } />
        //         </div>
        //       </div>
        //     )}
        //   </div>
        // );
      })}
    </>
  );

  if (layout === 'stacked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0' }}>
        {renderLabelAndAdd()}
        {activeConditions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {renderChips()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', padding: '4px 0' }}>
      {renderLabelAndAdd()}
      {renderChips()}
    </div>
  );
}